// The agent settings page (pages/agent/AgentSettingsPage): one read of everything the page
// shows, the Profile and Brand saves, KeyTa writing the brand profile, and the choices that
// save as they change. Every call is an action (services/actions/agent_settings.py) except the
// photo and logo uploads, which are routes (routers/agent_settings.py).
import api, { ApiError } from './api';
import { extractResponseData } from '../utils/apiResponseHandler';
import { rememberConnectProvider, runAction } from './agentOnboardingService';

/** The upload kinds: the agent's photo, their own logo, their brokerage's logo. */
export const MEDIA_KINDS = ['photo', 'logo', 'brokerage_logo'];

/**
 * Everything the page shows: { registration, brokerage, photo, contact, work, brand, ad_line,
 * notifications, social, account }. The board's records fill what the agent hasn't set.
 */
export const getSettings = () => runAction('agent.settings.get', {});

/** Save Profile fields (only the ones that changed); resolves to the whole settings again. */
export const updateProfile = (changes) => runAction('agent.settings.update_profile', changes);

/** Save Brand fields (only the ones that changed); resolves to the whole settings again. */
export const updateBrand = (changes) => runAction('agent.settings.update_brand', changes);

// One model call at a time: a second caller (React's double effect in development, a double
// click) shares the call already under way.
let writing = null;

// While another tab is writing it, the server answers { writing: true } and no draft: wait and ask
// for the draft that one is writing (without `again`, so no second model call; with `wait` after "Write
// again", so the draft already there is not taken for the new one), for up to a minute
// and a half.
export const BRAND_WAIT = { ms: 4000, times: 22 };
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const writeOrWait = async (again) => {
  let output = await runAction('agent.settings.write_brand', again ? { again: true } : {});
  for (let waits = 0; output?.writing && !output?.draft && waits < BRAND_WAIT.times; waits += 1) {
    await pause(BRAND_WAIT.ms);
    // After "Write again", `wait` keeps the draft already there from coming back as the new one.
    output = await runAction('agent.settings.write_brand', again ? { wait: true } : {});
  }
  return output;
};

/**
 * KeyTa writes the brand profile: resolves to { draft }. Without `again` the server hands back
 * a draft it already wrote, so opening the page twice costs one model call; one being written in
 * another tab is waited for.
 */
export const writeBrand = ({ again = false } = {}) => {
  if (writing && writing.again === again) return writing.promise;
  const promise = writeOrWait(again).finally(() => {
    if (writing && writing.promise === promise) writing = null;
  });
  writing = { again, promise };
  return promise;
};

/** The daily summary e-mail on or off; resolves to { daily_summary, summary_hour, right_away }. */
export const updateNotifications = (dailySummary) =>
  runAction('agent.settings.update_notifications', { daily_summary: Boolean(dailySummary) });

/** The posting rules (draft_kinds, ai_label); resolves to the social choices. */
export const updateSocial = (changes) => runAction('agent.settings.update_social', changes);

// ---- Facebook and Instagram (services/actions/social.py) ------------------------------------
// `owner` 'keyta': KeyTa's own accounts, where sellers' homes are posted (a super admin's, on the admin settings page).

/**
 * The agent's connected Instagram accounts and Facebook Pages, and which connections KeyTa
 * offers: { accounts: [...], available: { instagram, facebook } }.
 */
export const getSocialAccounts = (owner = 'agent') => runAction('social.accounts', owner === 'keyta' ? { owner } : {});

/**
 * Start "Connect Instagram" ('instagram') or "Connect Facebook & Instagram" ('facebook'): the
 * browser goes to Meta and comes back to `returnTo` with ?connected= or ?error=.
 */
export const startSocialConnect = async (kind, returnTo, owner = 'agent') => {
  const result = await runAction('social.connect_url', {
    kind, ...(returnTo ? { return_to: returnTo } : {}), ...(owner === 'keyta' ? { owner } : {}),
  });
  if (!result?.url) throw new Error('No connect URL returned');
  rememberConnectProvider(kind);
  window.location.assign(result.url);
};

/** Ask Meta now whether an account still works; resolves to the account. */
export const checkSocialAccount = (accountId) => runAction('social.check', { account_id: accountId });

/** Disconnect an account and forget its access; resolves to the account. */
export const disconnectSocialAccount = (accountId, owner = 'agent') => runAction(
  'social.disconnect', { account_id: accountId, ...(owner === 'keyta' ? { owner } : {}) },
);

/** The agent's photo as Settings shows it (their upload, else the board's): { url, source }. */
export const getPhoto = () => runAction('agent.settings.photo', {});

/** The agent's time zone (an IANA name); resolves to the account block. */
export const updateTimezone = (timezone) => runAction('agent.settings.update_timezone', { timezone });

const failed = async (response) => {
  const payload = await response.json().catch(() => ({}));
  return new ApiError(payload.message || payload.detail || 'Upload failed', payload.errors || [], response.status, payload);
};

/** Upload the photo or a logo (a File); resolves to { kind, url }. Replaces the earlier one. */
export const uploadMedia = async (kind, file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await api.authRequest(`/agent/settings/media/${kind}`, {
    method: 'POST',
    body: form,
    headers: { 'Content-Type': null },
  });
  if (!response.ok) throw await failed(response);
  return extractResponseData(await response.json());
};

/** Remove the agent's upload; the board's photo or office logo shows again where there is one. */
export const removeMedia = async (kind) => extractResponseData(await api.delete(`/agent/settings/media/${kind}`));

const agentSettingsService = {
  getSettings,
  getPhoto,
  updateProfile,
  updateBrand,
  writeBrand,
  updateNotifications,
  updateSocial,
  updateTimezone,
  uploadMedia,
  removeMedia,
  getSocialAccounts,
  startSocialConnect,
  checkSocialAccount,
  disconnectSocialAccount,
};

export default agentSettingsService;
